import { useState, useEffect, useCallback } from "react";
import { stravaApi } from "@/api/strava";
import { useAuth } from "./useAuth";

export function useStravaConnection() {
  const { isAuthenticated, isTokenReady } = useAuth();
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showPrompt, setShowPrompt] = useState<boolean>(false);

  // Check Strava connection status
  const checkConnection = useCallback(async () => {
    // Don't make API call if user is not authenticated or token isn't ready
    if (!isAuthenticated || !isTokenReady) {
      setIsConnected(false);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const status = await stravaApi.getConnectionStatus();
      setIsConnected(status.connected);
    } catch (error) {
      console.error("Failed to check Strava connection:", error);
      setIsConnected(false);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, isTokenReady]);

  // Check connection on mount and when authentication changes
  useEffect(() => {
    checkConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isTokenReady]);

  // Function to ensure Strava is connected before proceeding
  const ensureConnected = useCallback(
    (onConnected: () => void) => {
      if (isConnected) {
        onConnected();
      } else {
        setShowPrompt(true);
      }
    },
    [isConnected]
  );

  const closePrompt = useCallback(() => {
    setShowPrompt(false);
  }, []);

  const handleConnectionSuccess = useCallback(() => {
    setShowPrompt(false);
    setIsConnected(true);
  }, []);

  return {
    isConnected,
    isLoading,
    showPrompt,
    ensureConnected,
    closePrompt,
    handleConnectionSuccess,
    refetchConnection: checkConnection,
  };
}
